import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  IconBell,
  IconChevronDown,
  IconCloudOff,
  IconDeviceDesktop,
  IconDots,
  IconHelp,
  IconHome,
  IconLogout,
  IconMoon,
  IconQrcode,
  IconRefresh,
  IconSun,
  IconUser,
  IconUsers,
  IconWallet,
} from "@tabler/icons-react";
import { AgentStatus } from "@safiri/shared";
import { useI18n, type TKey } from "../i18n";
import { useAuth } from "../lib/auth";
import { useNotices } from "../lib/notices";
import { usePwa } from "../lib/pwa";
import { useTheme, type ThemePref } from "../lib/theme";
import { Alert, Avatar, cx, IconButton } from "./ui";
import { InstallBanner } from "./Install";

export function LanguageToggle({ onDark }: { onDark?: boolean }) {
  const { lang, setLang, t } = useI18n();
  return (
    <div role="radiogroup" aria-label={t("language")} className={cx("flex rounded-full p-0.5 text-xs font-semibold", onDark ? "bg-white/15" : "bg-sunken")}>
      {(["en", "sw"] as const).map((l) => (
        <button
          key={l}
          type="button"
          role="radio"
          aria-checked={lang === l}
          onClick={() => setLang(l)}
          className={cx(
            "min-w-[38px] rounded-full px-2.5 py-1.5 uppercase transition",
            lang === l ? (onDark ? "bg-white text-primary" : "bg-surface text-ink shadow-sm") : onDark ? "text-white/80" : "text-muted"
          )}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

const THEME_NEXT: Record<ThemePref, ThemePref> = { system: "light", light: "dark", dark: "system" };
const THEME_ICON = { system: IconDeviceDesktop, light: IconSun, dark: IconMoon };
const THEME_LABEL: Record<ThemePref, TKey> = { system: "themeSystem", light: "themeLight", dark: "themeDark" };

/** One tap cycles Auto → Light → Dark; the icon shows the current choice. */
export function ThemeToggle() {
  const { t } = useI18n();
  const { pref, setPref } = useTheme();
  const Icon = THEME_ICON[pref];
  const label = t("themeToggle", { mode: t(THEME_LABEL[pref]) });
  return (
    <IconButton label={label} onClick={() => setPref(THEME_NEXT[pref])} className="h-9 w-9 text-muted hover:text-ink">
      <Icon size={19} />
    </IconButton>
  );
}

/** Wordmark + "Agents". `onBrand` for the white version on the oxblood panel. */
export function Logo({ onBrand, to = "/" }: { onBrand?: boolean; to?: string }) {
  const { t } = useI18n();
  return (
    <Link to={to} className="flex items-center gap-2.5" aria-label={t("appName")}>
      {onBrand ? (
        <img src="/wordmark-white.png" alt="" className="h-[18px] w-auto" width={86} height={18} />
      ) : (
        <>
          <img src="/wordmark-ink.png" alt="" className="h-[18px] w-auto dark:hidden" width={86} height={18} />
          <img src="/wordmark-white.png" alt="" className="hidden h-[18px] w-auto dark:block" width={86} height={18} />
        </>
      )}
      <span className={cx("hidden rounded-md px-1.5 py-0.5 font-lemon text-[10px] tracking-[0.18em] min-[390px]:inline", onBrand ? "bg-white/15 text-white" : "bg-primary text-white")}>{t("agentsLabel")}</span>
    </Link>
  );
}

/** Offline and "new version" strips, shown above every layout. */
function SystemBanners() {
  const { t } = useI18n();
  const { online, updateReady, applyUpdate } = usePwa();
  return (
    <>
      {!online && (
        <div className="flex items-center justify-center gap-2 bg-ink px-4 py-2 text-center text-xs font-medium text-canvas" role="status">
          <IconCloudOff size={16} />
          {t("offlineBanner")}
        </div>
      )}
      {updateReady && (
        <div className="flex items-center justify-center gap-3 bg-info-soft px-4 py-2 text-sm text-ink" role="status">
          <span>{t("updateReady")}</span>
          <button type="button" onClick={applyUpdate} className="inline-flex items-center gap-1 font-semibold text-info underline underline-offset-2">
            <IconRefresh size={16} />
            {t("updateNow")}
          </button>
        </div>
      )}
    </>
  );
}

/**
 * Sign-in, apply and links from emails. Phones: a brand header and the form
 * below. Desktop: brand panel left, form right.
 */
export function AuthLayout({ children, wide }: { children: ReactNode; wide?: boolean }) {
  const { t } = useI18n();
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <SystemBanners />
      <div className="flex flex-1">
        <aside className="relative hidden w-[44%] max-w-xl flex-col justify-between overflow-hidden bg-primary p-10 text-white lg:flex">
          <img src="/img/pattern.png" alt="" className="pointer-events-none absolute -right-24 -top-24 w-[420px] opacity-[0.12] mix-blend-screen" aria-hidden />
          <Logo onBrand />
          <div className="relative">
            <img src="/img/refer.png" alt="" className="mb-6 h-48 w-auto drop-shadow-xl" />
            <h2 className="max-w-sm text-3xl font-semibold leading-tight">{t("brandHeadline")}</h2>
            <ul className="mt-6 flex flex-col gap-3 text-sm text-white/90">
              {(["brandPoint1", "brandPoint2", "brandPoint3"] as TKey[]).map((k, i) => (
                <li key={k} className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15 text-xs font-semibold">{i + 1}</span>
                  {t(k)}
                </li>
              ))}
            </ul>
          </div>
          <p className="relative text-xs text-white/60">© Smart Safiri</p>
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="pt-safe">
            <div className={cx("mx-auto flex items-center justify-between px-4 py-4 sm:px-6", wide ? "max-w-2xl" : "max-w-md")}>
              <span className="lg:invisible">
                <Logo />
              </span>
              <div className="flex items-center gap-1">
                <ThemeToggle />
                <LanguageToggle />
              </div>
            </div>
          </header>
          <main className={cx("mx-auto w-full flex-1 px-4 pb-10 pt-2 sm:px-6 lg:pt-10", wide ? "max-w-2xl" : "max-w-md")}>{children}</main>
        </div>
      </div>
    </div>
  );
}

const NAV: { to: string; key: TKey; icon: typeof IconHome; mobile: boolean }[] = [
  { to: "/", key: "navHome", icon: IconHome, mobile: true },
  { to: "/code", key: "navCode", icon: IconQrcode, mobile: true },
  { to: "/referrals", key: "navDrivers", icon: IconUsers, mobile: true },
  { to: "/wallet", key: "navWallet", icon: IconWallet, mobile: true },
  { to: "/notifications", key: "navNotifications", icon: IconBell, mobile: false },
  { to: "/profile", key: "navProfile", icon: IconUser, mobile: false },
  { to: "/help", key: "navHelp", icon: IconHelp, mobile: false },
  { to: "/more", key: "navMore", icon: IconDots, mobile: true },
];

function CountDot({ n, className }: { n: number; className?: string }) {
  if (n <= 0) return null;
  return <span className={cx("tabular flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white ring-2 ring-surface", className)}>{n > 99 ? "99+" : n}</span>;
}

/** Desktop account menu: who's signed in, Profile, Help, Sign out. Phones use the More tab. */
function AccountMenu() {
  const { t } = useI18n();
  const { me, signOut } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const base = "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium hover:bg-sunken";
  const item = cx(base, "text-ink");
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2.5 transition hover:bg-sunken"
      >
        <Avatar name={me?.name ?? ""} src={me?.profilePhotoUrl} size={32} />
        <span className="max-w-[140px] truncate text-sm font-semibold text-ink">{me?.name.split(" ")[0]}</span>
        <IconChevronDown size={16} className={cx("text-muted transition", open && "rotate-180")} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-40 mt-2 w-72 animate-pop-in rounded-2xl border border-line bg-surface p-2 shadow-raised">
          <div className="flex items-center gap-3 border-b border-line px-3 pb-3 pt-2">
            <Avatar name={me?.name ?? ""} src={me?.profilePhotoUrl} size={40} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink">{me?.name}</p>
              {me?.referralCode && <p className="font-mono text-xs tracking-wider text-muted">{me.referralCode}</p>}
            </div>
          </div>
          <div className="pt-2">
            <Link to="/profile" role="menuitem" className={item}>
              <IconUser size={18} className="text-muted" /> {t("navProfile")}
            </Link>
            <Link to="/help" role="menuitem" className={item}>
              <IconHelp size={18} className="text-muted" /> {t("navHelp")}
            </Link>
            <button type="button" role="menuitem" onClick={() => void signOut()} className={cx(base, "text-danger")}>
              <IconLogout size={18} /> {t("signOut")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Signed-in agents: sidebar + top bar on desktop, top bar + bottom tabs on phones. */
export function AppLayout() {
  const { t } = useI18n();
  const { me } = useAuth();
  const { unread } = useNotices();
  const { pathname } = useLocation();
  // "More" stays lit for the pages it lists.
  const moreActive = ["/more", "/profile", "/help", "/terms", "/notifications"].some((p) => pathname.startsWith(p));
  return (
    <div className="min-h-dvh bg-canvas">
      <SystemBanners />

      {/* Desktop sidebar: brand + navigation only */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-line bg-surface md:flex">
        <div className="flex h-16 items-center border-b border-line px-5">
          <Logo />
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4" aria-label={t("mainNav")}>
          {NAV.filter((n) => n.to !== "/more").map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                cx("flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition", isActive ? "bg-primary-soft text-brand" : "text-muted hover:bg-sunken hover:text-ink")
              }
            >
              <n.icon size={20} />
              <span className="flex-1">{t(n.key)}</span>
              {n.to === "/notifications" && <CountDot n={unread} className="ring-0" />}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="md:pl-64">
        {/* Top bar: logo on phones (no sidebar); on desktop the controls sit top right */}
        <header className="pt-safe sticky top-0 z-20 border-b border-line bg-surface/90 backdrop-blur-md">
          <div className="flex h-14 items-center justify-between gap-2 px-4 sm:px-6 md:h-16">
            <span className="md:hidden">
              <Logo />
            </span>
            <span className="hidden md:block" />
            <div className="flex items-center gap-0.5 md:gap-1.5">
              <ThemeToggle />
              <LanguageToggle />
              <Link to="/notifications" className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-sunken" aria-label={unread ? t("unreadNotifications", { n: unread }) : t("notifications")}>
                <IconBell size={22} />
                <CountDot n={unread} className="absolute right-0.5 top-0.5" />
              </Link>
              <div className="ml-1 hidden border-l border-line pl-3 md:block">
                <AccountMenu />
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-3xl px-4 pb-[calc(96px+env(safe-area-inset-bottom))] pt-5 sm:px-6 md:pb-12 md:pt-8">
          {me?.status === AgentStatus.SUSPENDED && (
            <div className="mb-5">
              <Alert tone="error" title={t("suspendedTitle")}>
                {t("statusSuspended")}
              </Alert>
            </div>
          )}
          <InstallBanner />
          <Outlet />
        </main>
      </div>

      {/* Phone bottom tabs */}
      <nav className="pb-safe fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/95 shadow-nav backdrop-blur-md md:hidden" aria-label={t("mainNav")}>
        <div className="grid grid-cols-5">
          {NAV.filter((n) => n.mobile).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) => {
                const on = n.to === "/more" ? moreActive : isActive;
                return cx("group relative flex flex-col items-center gap-1 pb-2 pt-2.5 text-[11px] font-medium transition", on ? "text-brand" : "text-subtle");
              }}
            >
              {({ isActive }) => {
                const on = n.to === "/more" ? moreActive : isActive;
                return (
                  <>
                    <span className={cx("relative flex h-8 w-14 items-center justify-center rounded-full transition", on && "bg-primary-soft")}>
                      <n.icon size={22} stroke={on ? 2.2 : 1.8} />
                      {n.to === "/more" && <CountDot n={unread} className="absolute -right-0.5 -top-1" />}
                    </span>
                    {t(n.key)}
                  </>
                );
              }}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

/** Draft / under review / rejected: the application and account basics, one column. */
export function ApplicantLayout() {
  const { t } = useI18n();
  const { signOut } = useAuth();
  return (
    <div className="min-h-dvh bg-canvas">
      <SystemBanners />
      <header className="pt-safe sticky top-0 z-20 border-b border-line bg-surface/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-2xl items-center justify-between gap-2 px-4 sm:h-16 sm:px-6">
          <Logo to="/application" />
          <div className="flex items-center gap-0.5">
            <ThemeToggle />
            <LanguageToggle />
            <NavLink to="/help" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-sunken" aria-label={t("navHelp")} title={t("navHelp")}>
              <IconHelp size={21} />
            </NavLink>
            <NavLink to="/profile" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-sunken" aria-label={t("navProfile")} title={t("navProfile")}>
              <IconUser size={21} />
            </NavLink>
            <IconButton label={t("signOut")} onClick={() => void signOut()}>
              <IconLogout size={21} />
            </IconButton>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-2xl px-4 pb-16 pt-6 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
